import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  useTheme,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import FlagIcon from '@mui/icons-material/Flag';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import DirectionsIcon from '@mui/icons-material/Directions';
import { openDirections } from '../../utils/openDirections';
import axios from 'axios';
import moment from 'moment';
import SavedItemsService from '../../services/savedItemsService';
import 'moment/locale/th';
import 'moment/locale/zh-cn';
import ReviewMediaCarousel from './ReviewMediaCarousel';
import EventMediaCarousel from './EventMediaCarousel';
import DualGroupChat from '../common/DualGroupChat';
import EventService from '../../services/eventService';

let useAuth;
try {
  const authModule = require('../Login/AuthContext');
  useAuth = authModule.useAuth;
} catch (e) {
  useAuth = () => ({ isAuthenticated: false, currentUser: null });
}

const API_URL = process.env.REACT_APP_API_URL;

const REPORT_REASONS = [
  { value: 'inappropriate', label: 'เนื้อหาไม่เหมาะสม' },
  { value: 'false_info', label: 'ข้อมูลเท็จหรือหลอกลวง' },
  { value: 'spam', label: 'สแปม / โฆษณาไม่พึงประสงค์' },
  { value: 'copyright', label: 'ละเมิดทรัพย์สินทางปัญญา' },
  { value: 'other', label: 'อื่นๆ' },
];

// Language-specific labels
const labels = {
  TH: { eventDate: 'เริ่มต้น', reviewDate: 'วันที่รีวิว', endDate: 'สิ้นสุด', website: 'เว็บไซต์' },
  EN: { eventDate: 'Start Date', reviewDate: 'Review Date', endDate: 'End Date', website: 'Website' },
  CN: { eventDate: '开始日期', reviewDate: '评论日期', endDate: '结束日期', website: '网站' },
};

const formatCount = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(n);
};

const PlaceDetails = ({
  place,
  selected,
  refProp,
  onLocationClick,
  language = 'TH',
  compact = false,
  suppressAutoScroll = false,
  isShiftingWindow = false,
  onExpand = null,
  hideParticipation = false,
  onCreatorClick = null,
}) => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth();

  // Participation state (optimistic)
  const [participationStatus, setParticipationStatus] = useState(null);
  const [participationLoading, setParticipationLoading] = useState(false);

  // Save state
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Share menu
  const [shareAnchor, setShareAnchor] = useState(null);

  // Comment dialog
  const [commentOpen, setCommentOpen] = useState(false);

  // Report dialog state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDesc, setReportDesc] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  // Scroll into view when selected
  if (selected === true && refProp?.current && !suppressAutoScroll && !isShiftingWindow) {
    refProp.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // Field normalization
  const eventName = place.event_name || place.name || 'Untitled Event';
  const eventDate = place.event_date || place.start_date;
  const eventEndDate = place.event_end_date || place.end_date;
  const eventLocation = place.event_location || place.location || place.address;
  const eventImage =
    place.generated_image_url ||
    place.image ||
    place.image_url ||
    place.cover_image_url ||
    place.poster_url ||
    place.event_image_link ||
    place?.photo?.images?.large?.url ||
    null;

  if (language === 'TH') moment.locale('th');
  else if (language === 'CN') moment.locale('zh-cn');
  else moment.locale('en');

  const currentLabels = labels[language] || labels.EN;
  const displayDate = (date) => (date ? moment(date).format('DD-MMM-YY') : '');

  const isReview = place._source === 'review';
  const isVenue = place._source === 'venue';
  const rawId = place.event_id || place.id;
  const numericEventId = (() => {
    if (typeof rawId === 'string' && rawId.startsWith('review_')) {
      return parseInt(rawId.replace('review_', ''), 10);
    }
    return parseInt(rawId, 10);
  })();

  // Save item type + ID (works for events, reviews, venues)
  const savedItemType = isReview ? 'review' : isVenue ? 'venue' : 'event';
  const savedItemId = (() => {
    if (typeof rawId === 'string' && rawId.startsWith('review_')) return rawId.replace('review_', '');
    return String(parseInt(rawId, 10) || rawId);
  })();

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }, []);

  // Load save status
  useEffect(() => {
    if (!isAuthenticated || !savedItemId) return;
    SavedItemsService.getSaveStatus(savedItemId, savedItemType, getAuthHeaders())
      .then(({ saved }) => setIsSaved(saved))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, savedItemId, savedItemType]);

  const handleToggleSave = useCallback(async () => {
    if (!isAuthenticated || saveLoading) return;
    setSaveLoading(true);
    try {
      const displayData = {
        item_name: place.event_name || place.name || place.place_name || '',
        item_image_url: place.generated_image_url || place.image || place.image_url || place.event_image_link || place.main_image_url || '',
        item_location: place.event_location || place.location || place.address || '',
        item_date: place.event_date || place.created_at || '',
      };
      const { saved } = await SavedItemsService.toggleSave(savedItemId, savedItemType, displayData, getAuthHeaders());
      setIsSaved(saved);
      setSnack({ open: true, msg: saved ? 'บันทึกแล้ว' : 'ยกเลิกการบันทึก', severity: 'success' });
    } catch (err) {
      console.error('[PlaceDetails] save error:', err);
    } finally {
      setSaveLoading(false);
    }
  }, [isAuthenticated, saveLoading, savedItemId, savedItemType, place, getAuthHeaders]);

  const handleDirectionsClick = useCallback((e) => {
    e?.stopPropagation?.();
    openDirections({
      latitude: place.latitude ?? place.lat,
      longitude: place.longitude ?? place.lng,
      address: place.event_location || place.location || place.address,
      language,
    });
  }, [place, language]);

  const creatorName = place.organizer || place.user_name || place.creator_name || place.reviewer_name || place.creator || (
    place.main_category === 'Venue' 
      ? (language === 'TH' ? 'ผู้ประกอบการ' : language === 'CN' ? '商家' : 'Merchant')
      : ''
  );
  const avatarLetter = creatorName ? creatorName.charAt(0).toUpperCase() : '?';

  // Participation toggle
  const handleParticipate = useCallback(async (targetStatus) => {
    if (!isAuthenticated || participationLoading) return;
    setParticipationLoading(true);
    try {
      const newStatus = participationStatus === targetStatus ? 'not-going' : targetStatus;
      await EventService.updateParticipationStatus(numericEventId, newStatus, getAuthHeaders);
      setParticipationStatus(newStatus === 'not-going' ? null : newStatus);
      setSnack({
        open: true,
        msg: newStatus === 'interested' ? 'บันทึก: สนใจ' : newStatus === 'going' ? 'บันทึก: จะไป' : 'ยกเลิกแล้ว',
        severity: 'success',
      });
    } catch (err) {
      console.error('[PlaceDetails] participation error:', err);
    } finally {
      setParticipationLoading(false);
    }
  }, [isAuthenticated, participationLoading, participationStatus, numericEventId, getAuthHeaders]);

  // Share
  const handleShareTo = useCallback((platform) => {
    const eventUrl = `${window.location.origin}/event/${rawId}`;
    const shareTitle = eventName;
    let url = '';
    switch (platform) {
      case 'facebook': url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl)}`; break;
      case 'twitter': url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(eventUrl)}&text=${encodeURIComponent(shareTitle)}`; break;
      case 'line': url = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(eventUrl)}`; break;
      case 'whatsapp': url = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareTitle + ' ' + eventUrl)}`; break;
      case 'copy':
        navigator.clipboard.writeText(eventUrl).then(() =>
          setSnack({ open: true, msg: 'คัดลอกลิงก์แล้ว', severity: 'success' })
        );
        break;
      default: break;
    }
    if (url) window.open(url, '_blank', 'width=600,height=400');
    setShareAnchor(null);
  }, [rawId, eventName]);

  // Report submit
  const handleReportSubmit = useCallback(async () => {
    if (!reportReason) return;
    setReportSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/reports`,
        {
          item_type: isReview ? 'review' : 'event',
          item_id: numericEventId,
          reason: reportReason,
          description: reportDesc,
        },
        getAuthHeaders()
      );
      setReportOpen(false);
      setReportReason('');
      setReportDesc('');
      setSnack({ open: true, msg: 'รายงานถูกส่งแล้ว ขอบคุณสำหรับการแจ้ง', severity: 'success' });
    } catch (err) {
      console.error('[PlaceDetails] report error:', err);
      setSnack({ open: true, msg: 'ไม่สามารถส่งรายงานได้ กรุณาลองใหม่', severity: 'error' });
    } finally {
      setReportSubmitting(false);
    }
  }, [reportReason, reportDesc, isReview, numericEventId, getAuthHeaders]);

  // ─── COMPACT LAYOUT ────────────────────────────────────────────────────────
  if (compact) {
    return (
      <Card
        elevation={0}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          backgroundColor: '#111827',
          boxShadow: '0 10px 26px rgba(17,24,39,0.16)',
        }}
      >
        {eventImage && (
          <CardMedia
            component="img"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            image={eventImage}
            loading="lazy"
            alt={eventName}
          />
        )}
        {!eventImage && (
          <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #111827, #374151)' }} />
        )}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(17,24,39,0.03) 30%, rgba(17,24,39,0.82) 100%)',
        }} />
        <Box sx={{
          position: 'absolute',
          left: 10,
          right: 10,
          bottom: 10,
          px: 1.1,
          py: 0.9,
          borderRadius: 2,
          backgroundColor: 'rgba(17, 24, 39, 0.62)',
          backdropFilter: 'blur(10px)',
          color: '#fff',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', lineHeight: 1.12, fontSize: '0.92rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={eventName}>{eventName}</Typography>
          {eventDate && <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.74rem', mt: 0.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayDate(eventDate)}</Typography>}
          {eventLocation && (
            <Box sx={{ cursor: 'pointer' }} onClick={() => onLocationClick?.()}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.86)', fontSize: '0.72rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', mt: 0.25 }} title={eventLocation}>{eventLocation}</Typography>
            </Box>
          )}
        </Box>
      </Card>
    );
  }

  // ─── STANDARD LAYOUT ───────────────────────────────────────────────────────
  const allImages = [eventImage, ...(place.additional_image_links || [])].filter(Boolean);
  const hasReviewMedia = isReview && place.media_items?.length >= 1;

  // Shared social bar (used inline below dates)
  const SocialBar = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 0.75,
        gap: 0.25,
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'grey.100',
        my: 1,
      }}
    >
      {/* Creator avatar */}
      <Tooltip title="See creator's profile">
        <Avatar sx={{ width: 26, height: 26, fontSize: '0.7rem', bgcolor: 'primary.light', color: 'primary.contrastText', cursor: 'pointer', mr: 0.25 }}>
          {avatarLetter}
        </Avatar>
      </Tooltip>

      {/* Follow */}
      <Tooltip title="Follow this creator">
        <IconButton size="small" sx={{ p: 0.5, color: 'text.secondary' }}>
          <PersonAddOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Box sx={{ flex: 1 }} />

      {/* Comment */}
      <Tooltip title="Comments">
        <IconButton size="small" sx={{ p: 0.5, color: 'text.secondary' }} onClick={() => setCommentOpen(true)}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      {/* Interested — auth only, hidden in list views */}
      {isAuthenticated && !hideParticipation && (
        <Tooltip title={participationStatus === 'interested' ? 'Remove from favourites' : 'Add to favourite'}>
          <IconButton size="small" sx={{ p: 0.5, color: participationStatus === 'interested' ? 'warning.main' : 'text.secondary' }} onClick={() => handleParticipate('interested')} disabled={participationLoading}>
            {participationStatus === 'interested' ? <StarIcon sx={{ fontSize: 18 }} /> : <StarBorderIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      )}

      {/* Going — auth only, hidden in list views */}
      {isAuthenticated && !hideParticipation && (
        <Tooltip title={participationStatus === 'going' ? 'Remove from going list' : 'Add to going list'}>
          <IconButton size="small" sx={{ p: 0.5, color: participationStatus === 'going' ? 'success.main' : 'text.secondary' }} onClick={() => handleParticipate('going')} disabled={participationLoading}>
            {participationStatus === 'going' ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : <EventAvailableIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      )}

      {/* Directions */}
      <Tooltip title={language === 'TH' ? 'นำทาง' : language === 'CN' ? '导航' : 'Directions'}>
        <IconButton size="small" sx={{ p: 0.5, color: 'text.secondary' }} onClick={handleDirectionsClick}>
          <DirectionsIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      {/* Save Button */}
      <Tooltip title={isSaved ? 'ยกเลิกการบันทึก' : 'บันทึก'}>
        <IconButton size="small" sx={{ p: 0.5, color: isSaved ? '#7c3aed' : 'text.secondary' }} onClick={handleToggleSave} disabled={saveLoading}>
          {isSaved ? <BookmarkIcon sx={{ fontSize: 18 }} /> : <BookmarkBorderIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Tooltip>

      {/* Share */}
      <Tooltip title="Share">
        <IconButton size="small" sx={{ p: 0.5, color: 'text.secondary' }} onClick={(e) => setShareAnchor(e.currentTarget)}>
          <ShareIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      {/* Report — auth only */}
      {isAuthenticated && (
        <Tooltip title="Report">
          <IconButton size="small" sx={{ p: 0.5, color: 'text.secondary' }} onClick={() => setReportOpen(true)}>
            <ReportProblemOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <>
      <Card elevation={6} sx={{ width: '90%', m: 2, borderRadius: 3, boxShadow: 3 }}>
        {/* Media */}
        {hasReviewMedia ? (
          <ReviewMediaCarousel mediaItems={place.media_items} altText={eventName} fallbackImage={eventImage} />
        ) : allImages.length > 0 ? (
          <EventMediaCarousel images={allImages} altText={eventName} />
        ) : null}

        <CardContent sx={{ pb: 0 }}>
          {creatorName && (
            <Box 
              onClick={(e) => {
                e.stopPropagation();
                if (onCreatorClick) {
                  onCreatorClick({
                    name: creatorName,
                    id: place.event_creator_id || place.user_id || place.venue_creator_id || place.reviewer_id || null
                  });
                }
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                p: '3px 10px',
                borderRadius: '20px',
                backgroundColor: 'rgba(124, 58, 237, 0.06)',
                border: '1px solid rgba(124, 58, 237, 0.12)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(124, 58, 237, 0.12)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              <Box sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: '#7c3aed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 800,
                fontSize: 10
              }}>
                {avatarLetter}
              </Box>
              <Typography sx={{ fontSize: '0.78rem', color: '#7c3aed', fontWeight: 750 }}>
                {creatorName}
              </Typography>
            </Box>
          )}
          {/* Title — no navigation */}
          <Typography gutterBottom variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            {eventName}
          </Typography>

          <Divider sx={{ my: 1 }} />

          {/* Dates — same horizontal row */}
          <Box sx={{ display: 'flex', gap: 1, my: 1, alignItems: 'stretch' }}>
            {eventDate && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {isReview ? currentLabels.reviewDate : currentLabels.eventDate}
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                  {displayDate(eventDate)}
                </Typography>
              </Box>
            )}
            {eventDate && eventEndDate && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}
            {eventEndDate && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {currentLabels.endDate}
                </Typography>
                <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                  {displayDate(eventEndDate)}
                </Typography>
              </Box>
            )}
          </Box>

          {/* ── Social action bar — immediately below dates ── */}
          {SocialBar}

          {/* Summary */}
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {place.event_summary || place.summary || place.description}
          </Typography>

          {/* View count + website */}
          <CardActions sx={{ justifyContent: 'space-between', alignItems: 'center', px: 0, pt: 1, pb: 0 }}>
            {place.view_count > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <VisibilityIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                  {formatCount(place.view_count)}
                </Typography>
              </Box>
            ) : <Box />}
            {(place.website_link || place.website) && (
              <Button size="small" variant="text" onClick={() => window.open(place.website_link || place.website, '_blank')} sx={{ color: 'primary.main', fontWeight: 600, textTransform: 'none', p: 0, minWidth: 0, fontSize: '0.78rem' }}>
                {currentLabels.website}
              </Button>
            )}
          </CardActions>

          {/* Location */}
          {eventLocation && (
            <Box sx={{ mt: 0.5, cursor: 'pointer' }} onClick={() => onLocationClick?.({ lat: place.latitude || place.lat, lng: place.longitude || place.lng })}>
              <Typography variant="subtitle2" color="primary" sx={{ fontSize: '0.85rem' }}>{eventLocation}</Typography>
            </Box>
          )}
        </CardContent>

        {/* ดูเพิ่มเติม */}
        <Button
          fullWidth
          variant="text"
          size="small"
          onClick={() => onExpand && onExpand(place)}
          sx={{
            mt: 1,
            borderTop: '1px solid',
            borderColor: 'grey.100',
            borderRadius: '0 0 12px 12px',
            py: 1,
            fontWeight: 700,
            fontSize: '0.85rem',
            color: 'primary.main',
            letterSpacing: 0.3,
            '&:hover': { bgcolor: 'primary.50' },
          }}
        >
          ดูเพิ่มเติม
        </Button>
      </Card>

      {/* ── Share menu ── */}
      <Menu anchorEl={shareAnchor} open={Boolean(shareAnchor)} onClose={() => setShareAnchor(null)} PaperProps={{ sx: { minWidth: 160 } }}>
        <MenuItem onClick={() => handleShareTo('copy')} sx={{ gap: 1 }}><ContentCopyIcon sx={{ fontSize: 16 }} /> คัดลอกลิงก์</MenuItem>
        <MenuItem onClick={() => handleShareTo('facebook')} sx={{ gap: 1 }}>Facebook</MenuItem>
        <MenuItem onClick={() => handleShareTo('twitter')} sx={{ gap: 1 }}>Twitter / X</MenuItem>
        <MenuItem onClick={() => handleShareTo('line')} sx={{ gap: 1 }}>LINE</MenuItem>
        <MenuItem onClick={() => handleShareTo('whatsapp')} sx={{ gap: 1 }}>WhatsApp</MenuItem>
      </Menu>

      {/* ── Comment dialog ── */}
      <Dialog open={commentOpen} onClose={() => setCommentOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { height: '70vh', display: 'flex', flexDirection: 'column' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }} noWrap>{eventName}</Typography>
          <IconButton size="small" onClick={() => setCommentOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: 'hidden', p: 1 }}>
          {isAuthenticated ? (
            <DualGroupChat eventId={numericEventId} chatType="interested" title="Comments" isAuthenticated={isAuthenticated} getAuthHeaders={getAuthHeaders} userGroups={null} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography color="text.secondary" textAlign="center">กรุณาเข้าสู่ระบบเพื่อแสดงความคิดเห็น</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Report dialog ── */}
      <Dialog
        open={reportOpen}
        onClose={() => { setReportOpen(false); setReportReason(''); setReportDesc(''); }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        {/* Coloured header */}
        <Box sx={{ bgcolor: 'error.main', px: 3, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FlagIcon sx={{ color: 'white', fontSize: 22 }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: 'white', flex: 1, fontSize: '1rem' }}>
            รายงานโพสต์นี้
          </Typography>
          <IconButton size="small" onClick={() => setReportOpen(false)} sx={{ color: 'rgba(255,255,255,0.8)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            กรุณาระบุเหตุผลที่ต้องการรายงานโพสต์นี้ ทีมงานจะตรวจสอบและดำเนินการโดยเร็วที่สุด
          </Typography>

          <FormControl component="fieldset" fullWidth required>
            <FormLabel component="legend" sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5, color: 'text.primary', '&.Mui-focused': { color: 'error.main' } }}>
              เหตุผล
            </FormLabel>
            <RadioGroup
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
            >
              {REPORT_REASONS.map((r) => (
                <FormControlLabel
                  key={r.value}
                  value={r.value}
                  control={<Radio size="small" color="error" />}
                  label={<Typography variant="body2">{r.label}</Typography>}
                  sx={{ mb: 0.25 }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            size="small"
            label="รายละเอียดเพิ่มเติม (ไม่บังคับ)"
            value={reportDesc}
            onChange={(e) => setReportDesc(e.target.value)}
            sx={{ mt: 2 }}
            inputProps={{ maxLength: 500 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
            {reportDesc.length}/500
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => { setReportOpen(false); setReportReason(''); setReportDesc(''); }}
            sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReportSubmit}
            disabled={!reportReason || reportSubmitting}
            sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            {reportSubmitting ? 'กำลังส่ง…' : 'ส่งรายงาน'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default React.memo(PlaceDetails);
